import { WorkItemTypes, WorkItemTypeValue } from 'common/workitems/constants/workitem-types';
import { WorkItemFactory } from './workitem-factory';
import { Workitem } from '../xislivechat/models/workitem';
import { from, merge, Observable } from 'rxjs';
import { bufferCount, distinctUntilChanged, filter, first, map, mergeMap, takeLast, takeUntil, tap, take, distinctUntilKeyChanged } from 'rxjs/operators';
import { TextChatMessagesStoreFactoryService } from '../text-chat-messages-store-factory/text-chat-messages-store-factory.service';
import { DynamicQueryEventTypes } from '../dynamicquery/dynamic-query-events.factory';
import { DynamicQueryEvent } from 'common/dynamicquery/dynamic-query-events.factory';
import { WorkItemBold } from './models/workitem-bold';
import { WorkItemDiscussion } from './models/work-item-discussion';
import { DynamicQueryDecoratorService } from '../dynamicquery/dynamic-query-decorator.service';
import { Injectable } from '@angular/core';
import { UserContextService } from 'common/user-context/user-context.service';
import { DynamicQueryAdapterService } from 'common/dynamicquery/dynamic-query-adapter.service';
import { DynamicQueryStoreObservableFactory } from 'common/dynamicquery/dynamic-query-store-observable';
import { DiscussionMessageGroupModel } from 'shared/services/message-group/message-group.model';
import { BcConfigService } from 'common/config/provider';
import { ChatMessagesStoreFactory } from 'common/chat-messages-store-factory/chat-messages-store-factory.service';
import { WorkItemHelper } from 'common/workitems/workitem-helper';
import { WorkItemChat } from 'common/workitems/models/work-item-chat';
import { WorkItemObservableCacheService } from 'components/work-item-lane-list/work-item-lane/work-item-widget/shared/services/work-item-observable-cache.service';
import { ChatItem } from 'common/workitems/interfaces/chat-item.interface';
import { Chat } from 'common/xislivechat/models/chat';
import { DynamicQueryObservable } from 'common/dynamicquery/dynamic-query-observable.model';
import { TextChatMessage } from 'common/xislivechat/models/text-chat-message';
import { WorkItem } from 'common/workitems/interfaces/workitem-base.interface';
import { ChatMessage } from 'common/xislivechat/models/chat-message';

export interface DynamicQueryEventsSubscription {
  insert: Observable<Workitem>;
  update: Observable<Workitem>;
  done: Observable<Workitem>;
  deleted: Observable<Workitem>;
  initialDone: Promise<any>;
}

@Injectable({
  providedIn: 'root'
})
export class WorkItemListenerService {
  public Messages;
  public static readonly CHAT_VIDEO_SESSION_STATUS_OBS_LIMIT = 10;

  constructor(private DynamicQuery: DynamicQueryAdapterService,
              private Decorator: DynamicQueryDecoratorService,
              private ChatMessageStoreFactory: ChatMessagesStoreFactory,
              private textChatMessagesStoreFactoryService: TextChatMessagesStoreFactoryService,
              private bcConfig: BcConfigService,
              private _userContextService: UserContextService,
              private DynamicQueryStoreObservableFactory: DynamicQueryStoreObservableFactory,
              private _workItemObservableCache: WorkItemObservableCacheService) {
  }

  createActiveRcHistoryItemsByLoginIdSubscriptions(): DynamicQueryEventsSubscription {
    const workItemObservables: DynamicQueryEventsSubscription = {
      insert: null,
      update: null,
      done: null,
      deleted: null,
      initialDone: null
    };

    const workItemObservable = this._observeActiveRcHistoryItemsByLoginId();

    workItemObservables.initialDone = this._observeActiveRcHistoryItemsByLoginId().pipe(first()).toPromise();

    workItemObservables.insert = workItemObservable.pipe(
      filter((dynamicQueryEvent: DynamicQueryEvent) => dynamicQueryEvent.event === DynamicQueryEventTypes.INSERTED),
      map((history: DynamicQueryEvent) => history.data)
    );

    workItemObservables.deleted = workItemObservable.pipe(
      filter((dynamicQueryEvent: DynamicQueryEvent) => dynamicQueryEvent.event === DynamicQueryEventTypes.DELETED),
      map((history: DynamicQueryEvent) => history.data)
    );

    return workItemObservables;
  }

  private _observeActiveRcHistoryItemsByLoginId() {
    const activeRcHistoryItemsObservable = this.DynamicQuery.getQuery('activeRcHistoryItemsByLoginID');
    return this.Decorator.create(activeRcHistoryItemsObservable, item => item);
  }

  // region BYLOGINID

  observeActiveWorkitemsByLoginId() {
    const activeWorkitemsObservable = this.DynamicQuery.getQuery('assignedWorkItemsByLoginID');
    return this.Decorator.create(activeWorkitemsObservable, Workitem.wrap);
  }

  observeActiveWorkItemsByLoginIdAndItemType(itemType: WorkItemTypeValue) {
    const activeWorkitemsObservable = this.DynamicQuery.getQuery('assignedWorkItemsByLoginIDAndItemType', {
      ItemType: itemType
    });
    return this.Decorator.create(activeWorkitemsObservable, Workitem.wrap);
  }

  observeAnswerableWorkItemsByLoginIdByWorkItemType(itemType: WorkItemTypes) {
    switch (itemType) {
      case WorkItemTypes.CHAT:
        return this.observeAnswerableChatWorkItemsByLoginId();
      case WorkItemTypes.EMAIL:
      case WorkItemTypes.SMS:
      case WorkItemTypes.FACEBOOK:
      case WorkItemTypes.ASYNC_MESSAGE:
      default:
        throw new Error('Please specify the correct work item type.');
    }
  }

  observeAnswerableChatWorkItemsByLoginId() {
    const answerableWorkItemsObservable = this.DynamicQuery.getQuery('getNextChats');
    return this.Decorator.create(answerableWorkItemsObservable, Workitem.wrap);
  }

  observeActiveDiscussionsByLoginId() {
    const activeDiscussionsObservable = this.DynamicQuery.getQuery('activeDiscussions', { OperatorID: this._userContextService.userContext.id });
    return this.Decorator.create(activeDiscussionsObservable, WorkItemDiscussion.wrap);
  }

  observeWorkitemsDoneByLoginId() {
    return this.observeActiveWorkitemsByLoginId().pipe(
      filter((dynamicQueryEvent: DynamicQueryEvent) => {
        return dynamicQueryEvent.event === DynamicQueryEventTypes.DONE;
      }),
      map(dynamicQueryEvent => {
        return Workitem.wrap(dynamicQueryEvent);
      })
    );
  }

  observeWorkitemsInsertsByLoginId() {
    return this.observeActiveWorkitemsByLoginId().pipe(
      filter((dynamicQueryEvent: DynamicQueryEvent) => {
        return dynamicQueryEvent.event === DynamicQueryEventTypes.INSERTED;
      }),
      map(dynamicQueryEvent => {
        return Workitem.wrap(dynamicQueryEvent);
      })
    );
  }

  observeWorkitemsDeletesByLoginId() {
    return this.observeActiveWorkitemsByLoginId().pipe(
      filter((dynamicQueryEvent: DynamicQueryEvent) => {
        return dynamicQueryEvent.event === DynamicQueryEventTypes.DELETED;
      }),
      map(dynamicQueryEvent => {
        return Workitem.wrap(dynamicQueryEvent);
      })
    );
  }

  observeWorkitemsUpdatesByLoginId() {
    return this.observeActiveWorkitemsByLoginId().pipe(
      filter((dynamicQueryEvent: DynamicQueryEvent) => dynamicQueryEvent.event === DynamicQueryEventTypes.UPDATED),
      map(dynamicQueryEvent => Workitem.wrap(dynamicQueryEvent))
    );
  }

  // endregion

  // region INSERT

  public createAcdBasedObservableForInsertByWorkItemType(workItemType: WorkItemTypes): Observable<WorkItem> {
    return this.observeWorkitemsInsertsByLoginId().pipe(
      filter(this._getFilterFunction(workItemType)),
      map(workItem => WorkItemFactory.create(workItemType, workItem))
    );
  }

  createTextChatAcdBasedObservableForInsert() {
    const observables = [];
    this.addSmsObservable(observables, this.createAcdBasedObservableForInsertByWorkItemType(WorkItemTypes.SMS));
    this.addFacebookObservable(observables, this.createAcdBasedObservableForInsertByWorkItemType(WorkItemTypes.FACEBOOK));
    this.addAsyncMessageObservable(observables, this.createAcdBasedObservableForInsertByWorkItemType(WorkItemTypes.ASYNC_MESSAGE));

    return merge(...observables);
  }

  createPromiseForInitialLoadingDone() {
    const observable = this.observeWorkitemsDoneByLoginId();
    return observable.pipe(first()).toPromise();
  }

  createObservableForChatMessagesByChatId(workItemId: string): DynamicQueryObservable<Workitem & ChatMessage> {
    const messagesSubs = this.DynamicQuery.getQuery('chatMessagesByChatID', { ChatID: workItemId });
    // code smell: is it intentional to create a workItem for each messages?
    return this.Decorator.create(messagesSubs, item => <Workitem & ChatMessage>Workitem.wrap(item));
  }

  createObservableForDiscussionsMessagesByChatId(workItemId: string): DynamicQueryObservable<Workitem & DiscussionMessageGroupModel> {
    const messagesSubs = this.DynamicQuery.getQuery('getDiscussionMessages', { WorkItemID: workItemId });
    // code smell: is it intentional to create a workItem for each messages?
    return this.Decorator.create(messagesSubs, item => <Workitem & DiscussionMessageGroupModel>Workitem.wrap(item));
  }

  // endregion

  // region REMOVE

  public createAcdBasedObservableForRemoveByWorkItemType(workItemType: WorkItemTypes): Observable<WorkItem> {
    return this.observeWorkitemsDeletesByLoginId().pipe(
      filter(this._getFilterFunction(workItemType)),
      map(workItem => WorkItemFactory.create(workItemType, workItem))
    );
  }

  createTextChatAcdBasedObservableForRemove() {
    const observables = [];
    this.addSmsObservable(observables, this.createAcdBasedObservableForRemoveByWorkItemType(WorkItemTypes.SMS));
    this.addFacebookObservable(observables, this.createAcdBasedObservableForRemoveByWorkItemType(WorkItemTypes.FACEBOOK));
    this.addAsyncMessageObservable(observables, this.createAcdBasedObservableForRemoveByWorkItemType(WorkItemTypes.ASYNC_MESSAGE));

    return merge(...observables);
  }

  // endregion

  // region UPDATE

  public createAcdBasedObservableForUpdateByWorkItemType(workItemType: WorkItemTypes): Observable<WorkItem> {
    return this.observeWorkitemsUpdatesByLoginId().pipe(
      filter(this._getFilterFunction(workItemType)),
      distinctUntilChanged((a: WorkItemBold, b: WorkItemBold) => {
        if (workItemType === WorkItemTypes.CHAT) {
          return a.Answered === b.Answered &&
            a.Ended === b.Ended &&
            a.ACDAssignmentExpires === b.ACDAssignmentExpires &&
            a.FolderID === b.FolderID &&
            a.Assigned === b.Assigned;
        } else {
          return false;
        }
      }),
      map(workItem => WorkItemFactory.create(workItemType, workItem))
    );
  }

  createTextChatAcdBasedObservableForUpdate() {
    const observables = [];
    this.addSmsObservable(observables, this.createAcdBasedObservableForUpdateByWorkItemType(WorkItemTypes.SMS));
    this.addFacebookObservable(observables, this.createAcdBasedObservableForUpdateByWorkItemType(WorkItemTypes.FACEBOOK));
    this.addAsyncMessageObservable(observables, this.createAcdBasedObservableForUpdateByWorkItemType(WorkItemTypes.ASYNC_MESSAGE));

    return merge(...observables);
  }

  // endregion

  addSmsObservable(observablesArray, observable) {
    if (this.bcConfig.current.features.smsMessages) {
      observablesArray.push(observable);
    }
  }

  addFacebookObservable(observablesArray, observable) {
    observablesArray.push(observable);
  }

  addAsyncMessageObservable(observablesArray, observable) {
    observablesArray.push(observable);
  }

  observeActiveChatsForVideoSessionStatus(): Observable<Chat> {
    let _chatVideoSessionStatusObsCount = 0;
    return this.createAcdBasedObservableForInsertByWorkItemType(WorkItemTypes.CHAT).pipe(
      filter(() => _chatVideoSessionStatusObsCount < WorkItemListenerService.CHAT_VIDEO_SESSION_STATUS_OBS_LIMIT),
      mergeMap((workItem: WorkItemChat) => {
        _chatVideoSessionStatusObsCount++;
        return this._workItemObservableCache.getWorkItemObservable(workItem.id, WorkItemTypes.CHAT).pipe(
          distinctUntilKeyChanged('VideoSessionStatus'),
          takeUntil(this.createAcdBasedObservableForRemoveByWorkItemType(WorkItemTypes.CHAT).pipe(
            filter((workItemToRemove: ChatItem) => workItemToRemove.id === workItem.id),
            tap(() => _chatVideoSessionStatusObsCount--),
            take(1)
          ))
        );
      })
    );
  }

  createObservableForWorkItemLastChatMessages(actualWorkitem) {
    return this.ChatMessageStoreFactory.create(actualWorkitem.id).pipe(
      mergeMap(chatMessages => this.createObservableFromLastWorkitemMessages(chatMessages)),
      map(chatMessages => this.createObservablesForWorkItems(actualWorkitem, chatMessages))
    );
  }

  createObservableForWorkItemDiscussionMessages(actualWorkItemId) {
    const messagesSubs = this.DynamicQuery.getQuery('getDiscussionMessages', { WorkItemID: actualWorkItemId });
    // code smell: is it intentional to create a workItem for each messages?
    const decoratedObservable = this.Decorator.create(messagesSubs, item => <Workitem & DiscussionMessageGroupModel>Workitem.wrap(item));
    return this.DynamicQueryStoreObservableFactory.create(decoratedObservable, (item1, item2) => item1.DiscussionMessageID === item2.DiscussionMessageID);
  }

  createObservableForWorkItemLastDiscussionMessages(actualWorkItem) {
    const messagesSubs = this.DynamicQuery.getQuery('getDiscussionMessages', { WorkItemID: actualWorkItem.WorkItemID });
    // code smell: is it intentional to create a workItem for each messages?
    const decoratedObservable = this.Decorator.create(messagesSubs, item => <Workitem & DiscussionMessageGroupModel>Workitem.wrap(item));
    return this.DynamicQueryStoreObservableFactory.create(decoratedObservable, (item1, item2) => item1.DiscussionMessageID === item2.DiscussionMessageID).pipe(
      mergeMap(discussionMessages => this.createObservableFromLastWorkitemMessages(discussionMessages)),
      map(discussionMessages => this.createObservablesForWorkItems(actualWorkItem, discussionMessages))
    );
  }

  createObservableForTextChatMessages(actualWorkItem): DynamicQueryObservable<Workitem & TextChatMessage> {
    const messageSubs = this.DynamicQuery.getQuery('textChatMessagesByTextChatID', { TextChatID: actualWorkItem.ItemID });
    // code smell: is it intentional to create a workItem for each messages?
    return this.Decorator.create(messageSubs, item => <Workitem & TextChatMessage>Workitem.wrap(item));
  }

  createObservableForWorkItemLastTextChats(actualWorkitem) {
    return this.textChatMessagesStoreFactoryService.create(actualWorkitem.id).pipe(
      mergeMap(messages => this.createObservableFromLastWorkitemMessages(messages)),
      map(messages => this.createObservablesForWorkItems(actualWorkitem, messages))
    );
  }

  createObservableFromLastWorkitemMessages(chatMessages) {
    return from(chatMessages).pipe(takeLast(2), bufferCount(2));
  }

  createObservablesForWorkItems(actualWorkItem, messages) {
    return {
      ItemID: actualWorkItem.id,
      Created: actualWorkItem.Created,
      messages: messages
    };
  }

  private _getFilterFunction(workItemType: WorkItemTypes) {
    return ((workItem) => workItem.ItemType === WorkItemHelper.convertItemTypeToDbEnum(workItemType));
  }
}
